import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { get } from 'lodash';
import { doesPropExistAndHasItChanged } from '@nexio/emvio-util-app';
import {
    EmvioLoadingSpinner as LoadingSpinner
} from '@nexio/emvio-util-ui';
import { getIsLoggedIn } from '../UserData/userDataEntity';
import { getSelf, getIsLoading as getIsSelfLoading, getErrorMessage as getSelfErrorMessage } from '../Self/selfEntity';
import { getSelf as fetchSelf } from '../Self/selfActions';
import { getCurrentUser, getCurrentUserTokens } from '../../utils/aws';
import { loginSuccess } from '../Login/loginActions';
import { historyShape, selfShape } from '../../utils/propTypes';
import Login from '../Login/Login';
import UserSetup from '../UserSetup/UserSetup';
import { fetchTimezone } from '../UserSettings/userSettingsActions';
import { getIsGetTimezoneLoading } from '../UserSettings/userSettingsEntity';
import NexioAgreement from '../AccountAgreement/NexioAgreement/NexioAgreement';
import PricingAgreement from '../AccountAgreement/PricingAgreement/PricingAgreement';
import SurchargeAgreement from '../AccountAgreement/SurchargeAgreement/SurchargeAgreement';
import { getGateways } from '../Gateways/gatewaysActions';
import { getIsGatewaysLoading } from '../Gateways/gatewaysEntity';
import { getApms } from '../Apms/apmActions';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import jwtDecode from 'jwt-decode';

export class AuthContainer extends Component {
    static propTypes = {
        children: PropTypes.node,
        history: historyShape,
        isLoggedIn: PropTypes.bool,
        isSelfLoading: PropTypes.bool,
        location: PropTypes.any,
        match: PropTypes.any,
        onGetSelf: PropTypes.func,
        onLoginSuccess: PropTypes.func,
        onUpdateSelf: PropTypes.func,
        self: selfShape,
        selfErrorMessage: PropTypes.string,
        onGetTimezone: PropTypes.func,
        isGetTimezoneLoading: PropTypes.bool,
        isGetGatewaysLoading: PropTypes.bool,
        onGetGateways: PropTypes.func,
        onGetApms: PropTypes.func,
        ldClient: PropTypes.object
    };

    state = {
        loginCheckComplete: false,
        isUserSetUpNeeded: false
    };

    componentDidMount() {
        this.determineIfUserIsLoggedIn();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.isLoggedIn && !this.props.isLoggedIn) {
            this.setState({ isUserSetUpNeeded: true });
        }

        if (!prevProps.isLoggedIn && this.props.isLoggedIn) {
            this.props.onGetTimezone();
            this.props.onGetGateways();
            this.props.onGetApms();
        }

        if (doesPropExistAndHasItChanged(prevProps, this.props, 'isLoggedIn') && this.props.isLoggedIn === true) {
            if (this.state.isUserSetUpNeeded) {
                this.props.onGetSelf();
            }
        }

        if (this.state.isUserSetUpNeeded && doesPropExistAndHasItChanged(prevProps, this.props, 'self')) {
            if (get(this.props.self, 'firstName') && get(this.props.self, 'lastName')) {
                this.setState({ isUserSetUpNeeded: false });
            }
        }

        // Identify user to LaunchDarkly when logged in and self data is available
        if (
            (!prevProps.isLoggedIn && this.props.isLoggedIn) ||
            (prevProps.self !== this.props.self && this.props.self)
        ) {
            this.identifyUserToLaunchDarkly();
        }
    }

    /**
     * Identify the current user to LaunchDarkly
     * This enables user-specific feature flag targeting
     */
    identifyUserToLaunchDarkly = () => {
        const { ldClient, self, isLoggedIn } = this.props;

        // Only identify if user is logged in and we have user data
        if (!ldClient || !isLoggedIn || !self) {
            return;
        }

        let userId = self.cognitoSub;

        // If cognitoSub is not in self data, extract it from the JWT idToken
        if (!userId) {
            getCurrentUserTokens().then((tokens) => {
                if (tokens && tokens.idToken) {
                    try {
                        const decodedToken = jwtDecode(tokens.idToken);
                        // 'sub' is the cognitoSub in Cognito JWT tokens
                        const extractedUserId = decodedToken.sub;

                        if (extractedUserId) {
                            this.performLaunchDarklyIdentify(extractedUserId, self);
                        }
                    } catch (decodeError) {
                        const showLaunchDarklyLogin = process.env.BUILD_ENV !== 'production' && process.env.BUILD_ENV !== 'sandbox';
                        if (showLaunchDarklyLogin) {
                            // eslint-disable-next-line no-console
                            console.error('Failed to decode token for LaunchDarkly identification:', decodeError);
                        }
                    }
                }
            }).catch((error) => {
                const showLaunchDarklyLogin = process.env.BUILD_ENV !== 'production' && process.env.BUILD_ENV !== 'sandbox';
                if (showLaunchDarklyLogin) {
                    // eslint-disable-next-line no-console
                    console.error('Failed to get tokens for LaunchDarkly identification:', error);
                }
            });
        } else {
            // cognitoSub is available, proceed with identification
            this.performLaunchDarklyIdentify(userId, self);
        }
    };

    /**
     * Perform the actual LaunchDarkly identify call
     * @param {string} userId - The user ID (cognitoSub) to identify
     * @param {object} self - The self user data object
     */
    performLaunchDarklyIdentify = (userId, self) => {
        const { ldClient } = this.props;
        const accountId = self.accountId;
        const email = self.userName;
        const displayName = `${self.firstName || ''} ${self.lastName || ''}`.trim();

        const showLaunchDarklyLogin = process.env.BUILD_ENV !== 'production' && process.env.BUILD_ENV !== 'sandbox';

        ldClient.identify({
            kind: 'user',
            key: String(userId),
            name: displayName,
            email: email,
            accountId: accountId,
            custom: {
                merchantId: self.merchantId,
                accountType: self.accountType,
                environment: process.env.BUILD_ENV
            }
        }).then(() => {
            if (showLaunchDarklyLogin) {
                // eslint-disable-next-line no-console
                console.log(`LaunchDarkly user identified: \n ${displayName || email} \n ${userId} (cognitoSub)`);
            }
        }).catch((error) => {
            if (showLaunchDarklyLogin) {
                // eslint-disable-next-line no-console
                console.error('LaunchDarkly identification error:', error);
            }
        });
    };

    determineIfUserIsLoggedIn = () => {
        if (getCurrentUser()) {
            getCurrentUserTokens().then(() => {
                this.props.onLoginSuccess();
                this.props.onGetSelf();
            }).catch(() => {
                // Suppress error
            }).then(() => {
                this.handleLoginCheckComplete();
            });
        } else {
            this.handleLoginCheckComplete();
            this.setState({ isUserSetUpNeeded: true });
        }
    };

    handleLoginCheckComplete = () => {
        this.setState({ loginCheckComplete: true });
    };

    render() {
        if (!this.state.loginCheckComplete) {
            // Still checking if user is logged in
            return <LoadingSpinner />;
        }

        if (!this.props.isLoggedIn && this.state.loginCheckComplete) {
            // User not logged in
            return <Login />;
        }

        if (this.props.isLoggedIn && this.state.loginCheckComplete) {
            // User is logged in
            const firstName = get(this.props.self, 'firstName');
            const lastName = get(this.props.self, 'lastName');
            const shouldShowPlatformAgreement = get(this.props.self, 'shouldShowPlatformAgreement');
            const shouldShowPricingAgreement = get(this.props.self, 'shouldShowPricingAgreement');
            const shouldShowSurchargeAgreement = get(this.props.self, 'shouldShowSurchargeAgreement');

            const { selfErrorMessage } = this.props;

            if (this.props.isSelfLoading || this.props.isGetTimezoneLoading || this.props.isGetGatewaysLoading) {
                return <LoadingSpinner />;
            } else if (!selfErrorMessage && (!firstName || !lastName)) {
                return <UserSetup />;
            } else if (!selfErrorMessage && shouldShowPlatformAgreement) {
                return <NexioAgreement />;
            } else if (!selfErrorMessage && shouldShowPricingAgreement) {
                return <PricingAgreement />;
            } else if (!selfErrorMessage && shouldShowSurchargeAgreement) {
                return <SurchargeAgreement />;
            } else {
                return this.props.children;
            }
        }
    }
}

function mapStateToProps(state) {
    return {
        isLoggedIn: getIsLoggedIn(state),
        isSelfLoading: getIsSelfLoading(state),
        isGetTimezoneLoading: getIsGetTimezoneLoading(state),
        self: getSelf(state),
        selfErrorMessage: getSelfErrorMessage(state),
        isGetGatewaysLoading: getIsGatewaysLoading(state)
    };
}

function mapDispatchToProps(dispatch) {
    return {
        onLoginSuccess: () => dispatch(loginSuccess()),
        onGetSelf: () => dispatch(fetchSelf()),
        onGetTimezone: () => dispatch(fetchTimezone()),
        onGetGateways: () => dispatch(getGateways()),
        onGetApms: () => dispatch(getApms())
    };
}

export default withLDConsumer()(
    withRouter(connect(mapStateToProps, mapDispatchToProps)(AuthContainer))
);
